import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getCompanyVehicles } from '../api/apiService';
import {
  AssignmentSelectionModal,
  AssignmentOption,
  AssignmentFetchResult,
} from './AssignmentSelectionModal';

interface Props {
  visible: boolean;
  onDismiss: () => void;
  onAssign: (vehicleIds: number[]) => Promise<void>;
  assignedVehicleIds: number[];
}

export const VehicleAssignmentModal: React.FC<Props> = ({
  visible,
  onDismiss,
  onAssign,
  assignedVehicleIds,
}) => {
  const { t } = useTranslation('manageDrivers');
  const fetchVehicles = useCallback(
    async (
      page: number,
      pageSize: number,
      search?: string
    ): Promise<AssignmentFetchResult> => {
      const response = await getCompanyVehicles(page, pageSize, search);
      if (!response.ok) {
        throw new Error(
          (response.data as any)?.detail ||
            t('vehicles.loadError', 'Unable to load vehicles')
        );
      }

      const data: any = response.data;
      const results: any[] = Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data)
        ? data
        : [];

      const items: AssignmentOption[] = results.map((vehicle) => {
        const vehicleInfo = [vehicle.make, vehicle.model, vehicle.year]
          .filter(Boolean)
          .join(' ');
        return {
          id: vehicle.id,
          title: vehicle.registration_number,
          description:
            vehicleInfo || t('vehicles.noDetails', 'No additional details'),
          icon: 'car',
        };
      });

      const totalPages =
        typeof data?.total_pages === 'number'
          ? data.total_pages
          : data?.count
          ? Math.max(1, Math.ceil(data.count / pageSize))
          : 1;

      return { items, totalPages };
    },
    [t]
  );

  return (
    <AssignmentSelectionModal
      visible={visible}
      onDismiss={onDismiss}
      onAssign={onAssign}
      assignedIds={assignedVehicleIds}
      fetchOptions={fetchVehicles}
      title={t('vehicles.assignVehicles', 'Assign Vehicles')}
      searchPlaceholder={t(
        'vehicles.searchVehicles',
        'Search by registration, make, model, or year...'
      )}
      emptySearchText={t(
        'vehicles.noVehiclesFound',
        'No vehicles found matching your search'
      )}
      emptyListText={t(
        'vehicles.noAvailableVehicles',
        'No available vehicles to assign'
      )}
      selectLabel={t('vehicles.select', 'Select')}
      selectedLabel={t('vehicles.selected', 'Selected')}
      selectedCountLabel={(count) =>
        `${count} ${t('vehicles.selected', 'selected')}`
      }
      assignButtonLabel={t('vehicles.assign', 'Assign')}
      cancelButtonLabel={t('cancel', 'Cancel')}
      loadErrorMessage={t(
        'vehicles.loadError',
        'Unable to load vehicles'
      )}
      selectionRequiredMessage={t(
        'vehicles.selectAtLeastOne',
        'Please select at least one vehicle'
      )}
      listTestId="vehicle-assignment-list"
    />
  );
};

